import { writable } from 'svelte/store';
import { DataStatus } from './common';
import { debouncer } from '@/Util/utils';
import { addConnectedWorkoutPlayer, getConnectedWorkoutPlayers, removeConnectedWorkoutPlayer, sendWorkoutToWorkoutPlayer, type BasicResponse, type SlopeIntervals, type WorkoutPlayer, type WorkoutPlayerType } from '@/Util/api';
import type { Exercise } from '@/Common/ActivityFunctions';
import { workoutPlayerTypeToName } from '@/Common/constants';
import { notifyApiError } from '@/Util/notify';
import { ErrorCode } from '@/Util/errorCodes';
import { askSlopeIntervals } from '@/Common/mixedMode';

/*
This singleton loads and manages the connected workout players for a user.
- Object must be initialized once in an onMount function for reactivity to work, preferably in a parent component.
*/

export interface ConnectedWorkoutPlayersWritable {
  status: DataStatus;
  players: WorkoutPlayer[];
}

export let hasSubscribers = false;

function createSingleton() {
  const { subscribe, set, update } = writable<ConnectedWorkoutPlayersWritable>({
    status: DataStatus.NEW,
    players: [],
  }, () => {
    hasSubscribers = true;
    return () => {
      hasSubscribers = false;
    };
  });
  
  async function load(): Promise<void> {
    const workoutPlayers = await getConnectedWorkoutPlayers();
  
    console.log('Singleton: Loaded connected workout players', workoutPlayers);
    set({
      status: DataStatus.READY,
      players: workoutPlayers,
    });
  }

  const loadDebouncer = debouncer(200);
  const executeLoadDebouncer = () => loadDebouncer.debounce(() => load());

  async function reactiveReload() {
    update((prev) => {
      return {
        status: DataStatus.RESYNCING,
        players: prev.players,
      };
    });
    executeLoadDebouncer();
  }

  async function init() {
    let isNew = false;
    update((prev) => {
      if (prev.status !== DataStatus.NEW) {
        return prev;
      }
      isNew = true;
      return {
        status: DataStatus.LOADING,
        players: [],
      };
    });
    // Only initialize once.
    if (!isNew) return;

    load();

    //@ts-ignore
    const Xert = window.Xert;
    Xert._userChannel.bind('add_connected_workout_player', reactiveReload);
    Xert._userChannel.bind('remove_connected_workout_player', reactiveReload);
    Xert._userChannel.bind('refresh_connected_workout_player', reactiveReload);
  }

  async function refresh() {
    update((prev) => {
      return {
        status: DataStatus.LOADING,
        players: prev.players,
      };
    });
    load();
  }

  /**
   * Sends `workout` to `workoutPlayer`, or clears the player when it's null. A mixed mode workout going
   * to a player without mixed mode support asks how to send its slope intervals, unless
   * `slopeIntervals` is given. The result has `cancelled` set when the rider dismisses that question.
   */
  async function doSend(workoutPlayer: WorkoutPlayerType, workout: Exercise | null, slopeIntervals?: SlopeIntervals): Promise<BasicResponse & { workout?: Exercise; cancelled?: boolean }> {
    if (workout?.path && !slopeIntervals) {
      const choice = await askSlopeIntervals(workoutPlayer, workout);
      if (choice === null) return { success: false, cancelled: true };
      slopeIntervals = choice;
    }

    // reflect sending state optimistically
    connectedWorkoutPlayers.update((prev) => {
      return { ...prev,
        players: prev.players.map((wp) => {
          if (wp.type === workoutPlayer) return { ...wp, workout: workout, status: workout == null ? 'reseting' : 'sending', updatedAt: new Date() };
          return wp;
        }),
      };
    });
    const result = await sendWorkoutToWorkoutPlayer(workoutPlayer, workout?.path, slopeIntervals);
    // reflect success/error state
    connectedWorkoutPlayers.update((prev) => {
      return { ...prev,
        players: prev.players.map((wp) => {
          if (wp.type === workoutPlayer) return { ...wp, workout: result.workout, status: !!result.success ? 'sent' : 'failed', updatedAt: new Date() };
          return wp;
        }),
      };
    });

    // Every caller renders the outcome as an icon or a "Failed" badge, neither of which says what
    // went wrong. Toast the reason here so all of them get it, rather than at each call site.
    if (!result.success) {
      const playerName = workoutPlayerTypeToName[workoutPlayer] ?? 'your app';
      notifyApiError(result, workout == null
        ? { code: ErrorCode.WORKOUT_PLAYER_CLEAR_FAILED, message: `Could not clear the workout sent to ${playerName}. Please try again in a moment.` }
        : { code: ErrorCode.WORKOUT_PLAYER_SEND_FAILED, message: `Could not send this workout to ${playerName}. Please try again, or reconnect it in your Sync settings.` });
    }

    return result;
  }

  async function addWorkoutPlayer(workoutPlayer: WorkoutPlayerType) {
    // reflect sending state optimistically
    connectedWorkoutPlayers.update((prev) => {
      // "as 'connecting'" for TS typechecking
      const newPlayers = [...prev.players, { type: workoutPlayer, status: 'connecting' as 'connecting', updatedAt: new Date() }];
      return { ...prev,
        players: newPlayers,
      };
    });
    const result = await addConnectedWorkoutPlayer(workoutPlayer);
    // reflect success/error state
    connectedWorkoutPlayers.update((prev) => {
      if (result.success) {
        return { ...prev,
          players: prev.players.map((wp) => {
            if (wp.type === workoutPlayer) return { ...wp, status: 'sent', workout: result.workout, updatedAt: new Date() };
            return wp;
          }),
        };
      } else {
        return { ...prev,
          players: prev.players.filter((wp) => {
            return !(wp.type === workoutPlayer && wp.status === 'connecting');
          }),
        };
      }
    });

    return result;
  }

  async function removeWorkoutPlayer(workoutPlayer: WorkoutPlayerType) {
    // reflect sending state optimistically
    connectedWorkoutPlayers.update((prev) => {
      return { ...prev,
        players: prev.players.filter((wp) => {
          return wp.type !== workoutPlayer;
        }),
      };
    });
    const result = await removeConnectedWorkoutPlayer(workoutPlayer);
    
    return result;
  }

  return {
    subscribe,
    set,
    update,
    init,
    sendWorkoutToWorkoutPlayer: doSend,
    refresh,
    addWorkoutPlayer,
    removeWorkoutPlayer,
  };
}

export const connectedWorkoutPlayers = createSingleton();